<template>
  <div class="w-full justify-end">
    <!-- Filter modal button -->
    <button class="rounded border p-2 pb-0" @click="showFilterModal">
      <img src="/images/admin/filter.svg" />
    </button>
    <ModalComponent
      :visible="isFilterModalVisible"
      title="Filter Options"
      size="large"
      class="filter-modal"
      @ok="applyFilters"
      @cancel="hideFilterModal"
    >
      <div class="filter-content">
        <div class="filter-columns">
          <!-- Age Range section -->
          <div class="filter-section">
            <h3 class="section-title">Age Range</h3>
            <div class="option-group">
              <v-checkbox
                v-for="option in ageOptions"
                :key="option.value"
                v-model="filters.ageRange"
                :value="option.value"
                :label="option.label"
                hide-details
                density="compact"
                color="#003d6a"
                class="filter-checkbox"
              />
            </div>
          </div>

          <!-- Topic section -->
          <div class="filter-section">
            <h3 class="section-title">Topic</h3>
            <div class="option-group">
              <v-checkbox
                v-for="topicOpt in topicOptions"
                :key="topicOpt"
                v-model="filters.topics"
                :value="topicOpt"
                :label="topicOpt"
                hide-details
                density="compact"
                color="#003d6a"
                class="filter-checkbox"
              />
            </div>
          </div>

          <!-- Subject Area section -->
          <div class="filter-section">
            <h3 class="section-title">Subject Area</h3>
            <div class="option-group">
              <v-checkbox
                v-for="option in subjectOptions"
                :key="option.value"
                v-model="filters.subjectAreas"
                :value="option.value"
                :label="option.label"
                hide-details
                density="compact"
                color="#003d6a"
                class="filter-checkbox"
              />
            </div>
          </div>

          <!-- Date Range section -->
          <div class="filter-section">
            <h3 class="section-title">Date Range</h3>
            <v-radio-group
              v-model="filters.period"
              hide-details
              density="compact"
              color="#003d6a"
              class="filter-radio-group"
              @update:model-value="selectDateRange"
            >
              <v-radio
                v-for="option in dateRangeOptions"
                :key="option.value"
                :label="option.label"
                :value="option.value"
                class="filter-radio"
              />
            </v-radio-group>

            <!-- Custom date pickers (show when custom is selected) -->
            <div v-if="filters.period === 'custom'" class="custom-date-pickers">
              <div class="date-field">
                <label class="date-field__label">From</label>
                <div class="date-field__input">
                  <svg
                    class="date-field__icon"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8 7V3M16 7V3M7 11H17M5 21H19C20.1046 21 21 20.1046 21 19V7C21 5.89543 20.1046 5 19 5H5C3.89543 5 3 5.89543 3 7V19C3 20.1046 3.89543 21 5 21Z"
                      stroke="#6C737F"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  <flat-pickr
                    v-model="customStartDate"
                    :config="startFlatpickrConfig"
                    class="date-picker"
                    placeholder="Select start date"
                  />
                </div>
              </div>
              <div class="date-field">
                <label class="date-field__label">To</label>
                <div class="date-field__input">
                  <svg
                    class="date-field__icon"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8 7V3M16 7V3M7 11H17M5 21H19C20.1046 21 21 20.1046 21 19V7C21 5.89543 20.1046 5 19 5H5C3.89543 5 3 5.89543 3 7V19C3 20.1046 3.89543 21 5 21Z"
                      stroke="#6C737F"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  <flat-pickr
                    v-model="customEndDate"
                    :config="endFlatpickrConfig"
                    class="date-picker"
                    placeholder="Select end date"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="filter-footer">
          <ActionButton
            variant="outline"
            custom-class="filter-footer__btn"
            @click="resetFilters"
          >
            Clear Filter
          </ActionButton>
          <ActionButton
            variant="primary"
            custom-class="filter-footer__btn"
            @click="applyFilters"
          >
            Apply
          </ActionButton>
        </div>
      </template>
    </ModalComponent>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, watch, defineEmits, computed } from "vue";
import ModalComponent from "./ModalComponent.vue";
import ActionButton from "@/components/common/ActionButton.vue";
import { sfiStore } from "@/services/sfiService";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";

import dayjs from "dayjs";

// Create reactive local state for filters
const isFilterModalVisible = ref(false);
const filters = reactive({
  ageRange: [],
  topics: [],
  subjectAreas: [],
  period: "custom", // Default to custom range to match sfiStore initial setup
});
const ageOptions = [
  { value: "5-10", label: "Ages 5-10" },
  { value: "10-14", label: "Ages 10-14" },
  { value: "14-19", label: "Ages 14-19" },
];

const topicOptions = [
  "Computational Thinking",
  "Data Science",
  "Design Thinking",
  "AI & Machine Learning",
  "Programming & Coding",
  "Simulation & Modeling",
];

const subjectOptions = [
  { value: "stem", label: "STEM" },
  { value: "humanities", label: "Humanities" },
  { value: "language-arts", label: "Language Arts" },
];

const dateRangeOptions = [
  { value: "allTime", label: "All Time" },
  { value: "7days", label: "Last 7 Days" },
  { value: "14days", label: "Last 14 Days" },
  { value: "30days", label: "Last 30 Days" },
  { value: "3months", label: "Last 3 Months" },
  { value: "6months", label: "Last 6 Months" },
  { value: "custom", label: "Custom Range" },
];

// Custom date variables for flatpickr
const customStartDate = ref(sfiStore.startDate);
const customEndDate = ref(sfiStore.endDate);

const startFlatpickrConfig = computed(() => {
  return {
    dateFormat: "Y-m-d",
    allowInput: true,
    altInput: true,
    altFormat: "j F Y",
    maxDate: customEndDate.value || "today",
  };
});

const endFlatpickrConfig = computed(() => {
  return {
    dateFormat: "Y-m-d",
    allowInput: true,
    altInput: true,
    altFormat: "j F Y",
    minDate: customStartDate.value || undefined,
    maxDate: "today",
  };
});

// Initialize filters based on sfiStore data
onMounted(() => {
  updateFiltersFromStore();
});

// Watch for date picker changes to update store values when in custom mode
watch([customStartDate, customEndDate], ([newStartDate, newEndDate]) => {
  if (filters.period === "custom" && newStartDate && newEndDate) {
    sfiStore.startDate = newStartDate;
    sfiStore.endDate = newEndDate;
  }
});

// Watch for store changes to sync filters
watch(
  () => [sfiStore.startDate, sfiStore.endDate],
  ([newStartDate, newEndDate]) => {
    updateDateRangeFilter();
    customStartDate.value = newStartDate;
    customEndDate.value = newEndDate;
  }
);

// Helper to update filters from store
const updateFiltersFromStore = () => {
  updateDateRangeFilter();
  customStartDate.value = sfiStore.startDate;
  customEndDate.value = sfiStore.endDate;

  if (sfiStore.age && sfiStore.age.length) {
    filters.ageRange = [...sfiStore.age];
  }
  if (sfiStore.topic && sfiStore.topic.length) {
    filters.topics = [...sfiStore.topic];
  }
  if (sfiStore.subject && sfiStore.subject.length) {
    filters.subjectAreas = [...sfiStore.subject];
  }
};

// Update date filter based on store dates
const updateDateRangeFilter = () => {
  const startDate = dayjs(sfiStore.startDate);
  const endDate = dayjs(sfiStore.endDate);
  const today = dayjs();

  if (endDate.isSame(today, "day")) {
    const diffDays = today.diff(startDate, "day");
    const diffMonths = today.diff(startDate, "month");

    if (diffDays === 6) {
      filters.period = "7days";
    } else if (diffDays === 13) {
      filters.period = "14days";
    } else if (diffDays === 29) {
      filters.period = "30days";
    } else if (diffMonths === 3) {
      filters.period = "3months";
    } else if (diffMonths === 6) {
      filters.period = "6months";
    } else if (startDate.isSame("2020-01-01")) {
      filters.period = "allTime";
    } else {
      filters.period = "custom";
    }
  } else {
    filters.period = "custom";
  }
};

// Show filter modal
const showFilterModal = () => {
  isFilterModalVisible.value = true;
};

// Hide modal without discarding in-progress selections
const hideFilterModal = () => {
  isFilterModalVisible.value = false;
};

// Apply filters to sfiStore, emit change event, and close the modal
const applyFilters = async () => {
  isFilterModalVisible.value = false;

  if (filters.period !== "custom") {
    selectDateRange(filters.period);
  }

  sfiStore.age = [...filters.ageRange];
  sfiStore.topic = [...filters.topics];
  sfiStore.subject = [...filters.subjectAreas];
  sfiStore.period = filters.period;
  sfiStore.startDate = customStartDate.value;
  sfiStore.endDate = customEndDate.value;

  try {
    await sfiStore.fetchData();
  } catch (error) {
    console.error("Error fetching data with filters:", error);
  }

  emit("filter", {
    ageRange: filters.ageRange,
    topics: filters.topics,
    subjectAreas: filters.subjectAreas,
    period: filters.period,
    startDate: sfiStore.startDate,
    endDate: sfiStore.endDate,
  });
};

// Reset filters to defaults and update store
const resetFilters = async () => {
  filters.ageRange = [];
  filters.topics = [];
  filters.subjectAreas = [];
  filters.period = "custom";

  sfiStore.age = [];
  sfiStore.subject = [];
  sfiStore.topic = [];
  sfiStore.period = "custom";
  sfiStore.startDate = dayjs().startOf("year").format("YYYY-MM-DD");
  sfiStore.endDate = dayjs().format("YYYY-MM-DD");

  customStartDate.value = sfiStore.startDate;
  customEndDate.value = sfiStore.endDate;

  try {
    await sfiStore.fetchData();
  } catch (error) {
    console.error("Error fetching data after reset:", error);
  }

  emit("filter", filters);
};

// Select a date range option
const selectDateRange = (range) => {
  if (range === "allTime") {
    filters.period = "allTime";
  } else {
    filters.period = range;
  }

  if (range !== "custom") {
    const today = dayjs();

    switch (range) {
      case "allTime":
        customStartDate.value = "2020-01-01";
        customEndDate.value = today.format("YYYY-MM-DD");
        break;
      case "7days":
        customStartDate.value = today.subtract(6, "day").format("YYYY-MM-DD");
        customEndDate.value = today.format("YYYY-MM-DD");
        break;
      case "14days":
        customStartDate.value = today.subtract(13, "day").format("YYYY-MM-DD");
        customEndDate.value = today.format("YYYY-MM-DD");
        break;
      case "30days":
        customStartDate.value = today.subtract(29, "day").format("YYYY-MM-DD");
        customEndDate.value = today.format("YYYY-MM-DD");
        break;
      case "3months":
        customStartDate.value = today.subtract(3, "month").format("YYYY-MM-DD");
        customEndDate.value = today.format("YYYY-MM-DD");
        break;
      case "6months":
        customStartDate.value = today.subtract(6, "month").format("YYYY-MM-DD");
        customEndDate.value = today.format("YYYY-MM-DD");
        break;
    }
  }
};

const emit = defineEmits(["filter"]);
</script>

<style scoped>
.w-full {
  width: 100%;
}

.justify-end {
  display: flex;
  justify-content: flex-end;
}

.rounded {
  border-radius: 0.25rem;
}

.border {
  border: 1px solid #e2e8f0;
}

.p-2 {
  padding: 0.5rem;
}

.pb-0 {
  padding-bottom: 0;
}

img {
  width: 24px;
  height: 24px;
}

/* Modal visual overrides */
:deep(.filter-modal .modal-content) {
  width: 1200px;
  max-width: 95vw;
  border-radius: 12px;
  box-shadow:
    0 2px 3px rgba(0, 0, 0, 0.1),
    0 6px 10px 4px rgba(0, 0, 0, 0.15);
}

:deep(.filter-modal .modal-header) {
  padding: 16px 32px 12px;
  border-bottom: none;
}

:deep(.filter-modal .modal-title) {
  font-family: var(--font-family-primary);
  font-size: var(--font-size-title-large);
  line-height: var(--line-height-title-large);
  font-weight: var(--font-weight-bold);
  color: var(--primary-blue);
}

:deep(.filter-modal .modal-close) {
  font-size: 28px;
  color: var(--black-body-text);
}

:deep(.filter-modal .modal-body) {
  padding: 0 32px;
}

:deep(.filter-modal .modal-footer) {
  border-top: none;
  padding: 12px 12px 16px;
}

/* Filter content */
.filter-content {
  width: 100%;
}

.filter-columns {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
  flex-wrap: wrap;
}

.filter-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1 1 160px;
  min-width: 0;
}

.section-title {
  font-family: var(--font-family-primary);
  font-size: 20px;
  line-height: 28px;
  font-weight: var(--font-weight-medium);
  color: #101828;
  letter-spacing: 0.03px;
  margin: 0;
}

.option-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Remove Vuetify's default wrapper padding / messages footer */
.filter-checkbox :deep(.v-input__control),
.filter-radio-group :deep(.v-input__control) {
  padding: 0;
}

.filter-checkbox :deep(.v-input__details),
.filter-radio-group :deep(.v-input__details) {
  display: none;
}

/* Checkbox — 40x40 state-layer, 24x24 box w/ 1px #CCC border, 4px radius */
.filter-checkbox :deep(.v-selection-control) {
  min-height: 40px;
  --v-selection-control-size: 40px;
}

.filter-checkbox :deep(.v-selection-control__wrapper) {
  width: 40px;
  height: 40px;
}

.filter-checkbox :deep(.v-selection-control__input) {
  width: 40px;
  height: 40px;
}

.filter-checkbox :deep(.v-selection-control__input::before) {
  display: none;
}

.filter-checkbox :deep(.v-selection-control__input > .v-icon) {
  opacity: 1;
  font-size: 24px;
  color: #ccc;
}

.filter-checkbox
  :deep(.v-selection-control--dirty .v-selection-control__input > .v-icon) {
  color: var(--primary-blue);
}

/* Radio — 48x48 state-layer */
.filter-radio :deep(.v-selection-control) {
  min-height: 48px;
  --v-selection-control-size: 48px;
}

.filter-radio :deep(.v-selection-control__wrapper) {
  width: 48px;
  height: 48px;
}

.filter-radio :deep(.v-selection-control__input) {
  width: 48px;
  height: 48px;
}

.filter-radio :deep(.v-selection-control__input::before) {
  display: none;
}

.filter-radio :deep(.v-selection-control__input > .v-icon) {
  opacity: 1;
  font-size: 24px;
  color: var(--neutral-700);
}

.filter-radio.v-selection-control--dirty
  :deep(.v-selection-control__input > .v-icon) {
  color: var(--primary-blue) !important;
}

.filter-radio-group :deep(.v-selection-control-group) {
  gap: 12px;
}

.filter-checkbox :deep(.v-label),
.filter-radio :deep(.v-label) {
  font-family: var(--font-family-primary);
  font-size: 16px;
  line-height: 24px;
  font-weight: var(--font-weight-medium);
  color: var(--black-body-text);
  letter-spacing: 0.024px;
  opacity: 1;
  padding-inline-start: 0;
}

/* Custom date pickers */
.custom-date-pickers {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 250px;
  margin-top: 4px;
}

.date-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

.date-field__label {
  font-family: var(--font-family-primary);
  font-size: 20px;
  line-height: 28px;
  font-weight: var(--font-weight-regular);
  color: var(--black-body-text);
  letter-spacing: 0.03px;
}

.date-field__input {
  display: flex;
  align-items: center;
  gap: 16px;
  height: 40px;
  padding: 4px 16px;
  border: 1px solid var(--neutral-700);
  border-radius: 8px;
  background-color: var(--white);
}

.date-field__icon {
  width: 24px;
  height: 24px;
}

.date-picker {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: var(--font-family-primary);
  font-size: 20px;
  line-height: 28px;
  font-weight: var(--font-weight-regular);
  color: var(--black-body-text);
  letter-spacing: 0.03px;
}

:deep(.date-field__input .flatpickr-input) {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  padding: 0;
  font-family: var(--font-family-primary);
  font-size: 20px;
  line-height: 28px;
  color: var(--black-body-text);
  letter-spacing: 0.03px;
}

:deep(.date-field__input .date-picker) {
  border: none;
  outline: none;
}

/* Footer buttons */
.filter-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  width: 100%;
}

:deep(.filter-footer__btn) {
  min-height: 56px;
  padding: 10px 16px;
  border-radius: 12px;
}

:deep(.filter-footer__btn .action-button__text) {
  font-size: 20px;
  line-height: 28px;
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.03px;
}

:deep(.filter-footer__btn.action-button--outline) {
  border-color: var(--black-body-text);
  color: var(--black-body-text);
}
</style>
